import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { errorToast } from "@/lib/utils/ToastUtils";
import { toast } from "sonner";
import { useOrg } from "@/lib/states/session/hooks";
import { trpc } from "@/lib/trpc/client";
import { z } from "zod";
import { useWatch } from "react-hook-form";
import { useZodForm } from "@/lib/forms/useZodForm";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import DialogAlertBlocker from "./DialogAlertBlocker";
import { trpcVanilla } from "@/lib/trpc/vanilla";

export type PackageRecord = Awaited<
  ReturnType<typeof trpcVanilla.packages.list.query>
>[number];

interface PackageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: PackageRecord;
  onSaved?: (saved: PackageRecord) => void;
}

const currencyOptions = ["GBP", "USD", "EUR", "AUD", "NZD"] as const;
type CurrencyCode = (typeof currencyOptions)[number];
const planTypeOptions = ["weekly", "monthly", "yearly", "one-off"];
const NO_ONBOARDING_FORM = "__no_onboarding_form__";

// map currency code to symbol for clarity and reuse
const currencySymbols: Record<string, string> = {
  GBP: "£",
  USD: "$",
  EUR: "€",
  AUD: "$",
  NZD: "$",
};

const getCurrencySymbol = (code?: string) => currencySymbols[code ?? ""] ?? "";

const euroRegions = new Set([
  "AT",
  "BE",
  "CY",
  "DE",
  "EE",
  "ES",
  "FI",
  "FR",
  "GR",
  "IE",
  "IT",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "PT",
  "SI",
  "SK",
]);

const regionToCurrency: Record<string, CurrencyCode> = {
  GB: "GBP",
  US: "USD",
  AU: "AUD",
  NZ: "NZD",
};

const getPreferredCurrency = (): CurrencyCode | null => {
  if (typeof navigator === "undefined") {
    return null;
  }

  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone === "Europe/London") {
      return "GBP";
    }
    if (timeZone?.startsWith("Europe/")) {
      return "EUR";
    }
    if (timeZone?.startsWith("America/")) {
      return "USD";
    }
    if (timeZone?.startsWith("Australia/")) {
      return "AUD";
    }
    if (timeZone === "Pacific/Auckland") {
      return "NZD";
    }
  } catch (error) {
    console.warn("Unable to resolve timezone for currency ordering", error);
  }

  const locale =
    (navigator.languages && navigator.languages[0]) || navigator.language;
  const localeRegion = locale?.split("-")[1]?.toUpperCase();

  if (localeRegion) {
    if (regionToCurrency[localeRegion]) {
      return regionToCurrency[localeRegion];
    }
    if (euroRegions.has(localeRegion)) {
      return "EUR";
    }
  }

  return null;
};

const formatCurrency = (
  amount: number | null | undefined,
  currency?: string
) => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount)))
    return "";
  try {
    // show no decimal places for whole-unit prices by default
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency ?? "USD",
      maximumFractionDigits: 0,
      // prefer narrow symbol (€, £) when available
      currencyDisplay: "narrowSymbol",
    }).format(Number(amount));
  } catch (_e) {
    // fallback to simple formatting
    return `${getCurrencySymbol(currency)}${Number(amount).toLocaleString()}`;
  }
};

const schema = z
  .object({
    name: z.string().min(1, "Name is required"),
    description: z.string().optional().nullable(),
    currency: z.enum(["USD", "GBP", "EUR", "AUD", "NZD"]).default("USD"),
    plan_type: z.enum(["weekly", "monthly", "yearly", "one-off"]).default("weekly"),
    // 0 represents "Until cancelled"
    duration_value: z.number().min(0).default(1),
    duration_unit: z.enum(["Weeks", "Months", "Years"]).default("Weeks"),
    price_amount: z.coerce.number().min(0).default(0),
    free_trial: z.boolean().default(false),
    trial_days: z.coerce.number().int().min(0).default(0),
    initial_fee: z.boolean().default(false),
    initial_fee_amount: z.coerce.number().min(0).default(0),
    instant_access: z.boolean().default(false),
    onboarding_form_id: z.string().nullable().optional(),
  })
  .superRefine((values, ctx) => {
    if (values.free_trial && values.trial_days < 1) {
      ctx.addIssue({
        code: "custom",
        message: "Trial days must be at least 1",
        path: ["trial_days"],
      });
    }
    if (values.initial_fee && values.initial_fee_amount <= 0) {
      ctx.addIssue({
        code: "custom",
        message: "Initial fee must be greater than 0",
        path: ["initial_fee_amount"],
      });
    }
  });

type PackageFormValues = z.infer<typeof schema>;

const PackageDialog: React.FC<PackageDialogProps> = ({
  open,
  onOpenChange,
  initial,
  onSaved,
}) => {
  const org = useOrg();

  const [isSaving, setIsSaving] = useState(false);
  const [alertBlockerOpen, setAlertBlockerOpen] = useState(false);
  const [orderedCurrencyOptions, setOrderedCurrencyOptions] = useState<
    CurrencyCode[]
  >([...currencyOptions]);
  const preferredCurrency = useMemo(() => getPreferredCurrency(), []);

  const form = useZodForm(schema, {
    defaultValues: {
      name: "",
      description: "",
      currency: "USD",
      plan_type: "weekly",
      duration_value: 1,
      duration_unit: "Weeks",
      price_amount: 0,
      free_trial: false,
      trial_days: 0,
      initial_fee: false,
      initial_fee_amount: 0,
      instant_access: false,
      onboarding_form_id: null,
    },
  });
  const upsertPackage = trpc.packages.upsert.useMutation();
  const { data: onboardingForms } = trpc.packages.listOnboardingForms.useQuery(
    undefined,
    { enabled: open && Boolean(org?.id) },
  );
  useEffect(() => {
    // If dialog is closed, always reset to defaults to avoid stale values
    if (!open) {
      form.reset();
      return;
    }
    if (initial) {
      const pt = initial.plan_type?.toLowerCase?.() ?? "weekly";
      form.reset({
        name: initial.name ?? "",
        description: initial.description ?? "",
        currency: (initial.currency as any) ?? "USD",
        plan_type: pt as any,
        duration_value: initial.duration_count ?? 1,
        duration_unit: pt === "yearly" ? "Years" : pt === "monthly" ? "Months" : "Weeks",
        price_amount: initial.price != null ? Number(initial.price) / 100 : 0,
        free_trial: !!initial.free_trial,
        trial_days: initial.trial_days ?? 0,
        initial_fee: !!initial.initial_fee,
        initial_fee_amount:
          initial.initial_fee_amount != null
            ? Number(initial.initial_fee_amount) / 100
            : 0,
        instant_access: !!initial.instant_access,
        onboarding_form_id: initial.onboarding_form_id ?? null,
      });
    } else {
      form.reset({
        name: "",
        description: "",
        currency: preferredCurrency ?? "USD",
        plan_type: "weekly",
        duration_value: 1,
        duration_unit: "Weeks",
        price_amount: 0,
        free_trial: false,
        trial_days: 0,
        initial_fee: false,
        initial_fee_amount: 0,
        instant_access: false,
        onboarding_form_id: null,
      });
    }
  }, [initial, open, preferredCurrency]);

  useEffect(() => {
    if (!open || initial) {
      setOrderedCurrencyOptions([...currencyOptions]);
      return;
    }

    if (!preferredCurrency || !currencyOptions.includes(preferredCurrency)) {
      setOrderedCurrencyOptions([...currencyOptions]);
      return;
    }

    const reordered = [
      preferredCurrency,
      ...currencyOptions.filter((currency) => currency !== preferredCurrency),
    ];
    setOrderedCurrencyOptions(reordered);
  }, [initial, open]);

  const watched = useWatch({ control: form.control });

  // Keep duration_unit in sync with plan_type (Weeks for Weekly, Months for Monthly).
  useEffect(() => {
    if (watched.plan_type === "weekly") form.setValue("duration_unit", "Weeks");
    if (watched.plan_type === "monthly")
      form.setValue("duration_unit", "Months");
    if (watched.plan_type === "one-off")
      form.setValue("duration_unit", "Weeks");
    if (watched.plan_type === "yearly") {
      form.setValue("duration_unit", "Years");
      const current = form.getValues("duration_value");
      if (current > 5) form.setValue("duration_value", 0);
    }
  }, [watched.plan_type]);

  useEffect(() => {
    if (
      (watched.plan_type === "one-off") &&
      watched.free_trial
    ) {
      form.setValue("free_trial", false, { shouldDirty: true });
      form.setValue("trial_days", 0, { shouldDirty: true });
    }
  }, [watched.plan_type, watched.free_trial, form]);

  const totalPayout = useMemo(() => {
    const price = Number(watched.price_amount ?? 0);
    const dur = Number(watched.duration_value ?? 0);
    if (dur === 0 || watched.plan_type === "one-off") return price;
    return dur * price;
  }, [watched.duration_value, watched.price_amount]);

  const handleOnOpenChange = (openValue: boolean) => {
    if (form.formState.isDirty && !form.formState.isSubmitSuccessful) {
      setAlertBlockerOpen(true);
      return;
    }
    onOpenChange(openValue);
  };

  const onSubmit = async (values: PackageFormValues) => {
    if (!org?.id) {
      errorToast(
        new Error("Missing organisation"),
        "Please ensure you belong to an organisation"
      );
      return;
    }

    setIsSaving(true);

    try {
      const saved = await upsertPackage.mutateAsync({
        id: initial?.id,
        name: values.name,
        description: values.description ?? null,
        currency: values.currency,
        planType: values.plan_type,
        durationCount: values.duration_value,
        priceAmount: Number(values.price_amount ?? 0),
        freeTrial: values.free_trial,
        trialDays: Math.round(Number(values.trial_days ?? 0)),
        initialFee: values.initial_fee,
        initialFeeAmount: Number(values.initial_fee_amount ?? 0),
        instantAccess: values.instant_access,
        onboardingFormId: values.onboarding_form_id || null,
      });
      toast.success(initial?.id ? "Package updated" : "Package created");
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      console.error("Error saving package", err);
      errorToast(
        err instanceof Error ? err : new Error("Failed to save package"),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <DialogAlertBlocker
        shouldBlock={alertBlockerOpen}
        proceed={() => {
          setAlertBlockerOpen(false);
          onOpenChange(false);
        }}
        cancel={() => setAlertBlockerOpen(false)}
      />
      <Dialog
        open={open}
        onOpenChange={handleOnOpenChange}
        analyticsName="package_dialog"
      >
        <DialogContent className="max-w-2xl">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <DialogHeader>
                <DialogTitle>
                  {initial?.id ? "Edit package" : "Setup A New Package"}
                </DialogTitle>
                <DialogDescription>
                  Create or edit a package that clients can purchase.
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-1 gap-4 mt-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Package Name</FormLabel>
                      <FormControl>
                        <Input placeholder="12 week program" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Package Description</FormLabel>
                      <FormControl>
                        <Textarea
                          rows={3}
                          placeholder="Describe the package"
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-4 gap-3 items-end">
                  <FormField
                    control={form.control}
                    name="currency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Currency</FormLabel>
                        <FormControl>
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {orderedCurrencyOptions.map((c) => (
                                <SelectItem key={c} value={c}>
                                  {c}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="plan_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Type of plan</FormLabel>
                        <FormControl>
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {planTypeOptions.map((p) => (
                                <SelectItem key={p} value={p}>
                                  {p.charAt(0).toUpperCase() + p.slice(1)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div>
                    <FormLabel>For how long?</FormLabel>
                    <FormField
                      control={form.control}
                      name="duration_value"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Select
                              value={String(field.value ?? 1)}
                              onValueChange={(v) => field.onChange(Number(v))}
                              // disable the selector for one-off plans
                              disabled={watched.plan_type === "one-off"}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {watched.plan_type === "one-off" && (
                                  <SelectItem
                                    value={String(watched.duration_value)}
                                  >
                                    One-off
                                  </SelectItem>
                                )}
                                {watched.plan_type === "yearly" && (
                                  <>
                                    <SelectItem value="0">Until cancelled</SelectItem>
                                    {[1, 2, 3, 4, 5].map((n) => (
                                      <SelectItem key={n} value={String(n)}>
                                        {n} {n === 1 ? "year" : "years"}
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                                {watched.plan_type !== "one-off" && watched.plan_type !== "yearly" && (
                                  <>
                                    <SelectItem value={"0"}>
                                      Until cancelled
                                    </SelectItem>
                                    {(watched.plan_type === "monthly"
                                      ? Array.from({ length: 24 }, (_, i) => i + 1)
                                      : Array.from({ length: 52 }, (_, i) => i + 1)
                                    ).map((n) => {
                                      const unitLabel =
                                        watched.plan_type === "monthly"
                                          ? n > 1
                                            ? "months"
                                            : "month"
                                          : n > 1
                                            ? "weeks"
                                            : "week";
                                      const yearLabel =
                                        watched.plan_type === "monthly" && n === 12
                                          ? " (1 year)"
                                          : watched.plan_type === "monthly" && n === 24
                                            ? " (2 years)"
                                            : "";
                                      return (
                                        <SelectItem key={n} value={String(n)}>
                                          {n} {unitLabel}{yearLabel}
                                        </SelectItem>
                                      );
                                    })}
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div>
                    <FormLabel>Price</FormLabel>
                    <FormField
                      control={form.control}
                      name="price_amount"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            {/* input group: wrapper provides the border/background so prefix/suffix don't overlap */}
                            <div className="flex items-center rounded-md border border-input bg-background overflow-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background">
                              <span className="pl-3 py-2 text-sm text-foreground/90 font-medium">
                                {getCurrencySymbol(watched.currency)}
                              </span>
                              <Input
                                type="number"
                                data-role="price-input"
                                {...field}
                                className="flex-1 px-1 py-2"
                                style={{
                                  border: 0,
                                  boxShadow: "none",
                                  outline: "none",
                                  background: "transparent",
                                  borderRadius: 0,
                                }}
                                aria-label="Price amount"
                              />
                              <span className="pr-3 py-2 text-sm text-foreground/70">
                                {watched.plan_type === "one-off"
                                  ? "one-off"
                                  : watched.plan_type === "yearly"
                                    ? "per year"
                                    : `per ${watched.plan_type === "monthly" ? "month" : "week"}`}
                              </span>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <div className="flex flex-wrap items-center gap-4">
                    <FormField
                      control={form.control}
                      name="free_trial"
                      render={({ field }) => {
                        const isOneOff = watched.plan_type === "one-off";
                        return (
                          <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={(checked) => {
                                  field.onChange(checked);
                                  if (!checked) {
                                    form.setValue("trial_days", 0, {
                                      shouldDirty: true,
                                    });
                                  }
                                }}
                                disabled={isOneOff}
                              />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              {isOneOff ? (
                                <TooltipProvider delayDuration={200}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-block cursor-not-allowed">
                                        <FormLabel>
                                          Include free trial
                                        </FormLabel>
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>
                                        Free trials are not available for
                                        one-off packages.
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <FormLabel>Include free trial</FormLabel>
                              )}
                            </div>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />

                    {watched.free_trial ? (
                      <FormField
                        control={form.control}
                        name="trial_days"
                        render={({ field }) => (
                          <FormItem className="flex items-center space-x-2 space-y-0">
                            <FormLabel className="whitespace-nowrap">
                              Trial days
                            </FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                placeholder="14"
                                {...field}
                                className="w-24"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ) : null}
                  </div>

                  <div className="flex flex-wrap items-center gap-4">
                    <FormField
                      control={form.control}
                      name="initial_fee"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={(checked) => {
                                field.onChange(checked);
                                if (!checked) {
                                  form.setValue("initial_fee_amount", 0, {
                                    shouldDirty: true,
                                  });
                                }
                              }}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>Include initial fee</FormLabel>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {watched.initial_fee ? (
                      <FormField
                        control={form.control}
                        name="initial_fee_amount"
                        render={({ field }) => (
                          <FormItem className="flex items-center space-x-2 space-y-0">
                            <FormLabel className="whitespace-nowrap">
                              Amount
                            </FormLabel>
                            <FormControl>
                              <div className="flex items-center rounded-md border border-input bg-background overflow-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background">
                                <span className="pl-3 py-2 text-sm text-foreground/90 font-medium">
                                  {getCurrencySymbol(watched.currency)}
                                </span>
                                <Input
                                  type="number"
                                  min={0}
                                  {...field}
                                  className="w-28 px-1 py-2"
                                  style={{
                                    border: 0,
                                    boxShadow: "none",
                                    outline: "none",
                                    background: "transparent",
                                    borderRadius: 0,
                                  }}
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ) : null}
                  </div>

                  <FormField
                    control={form.control}
                    name="instant_access"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            Give clients instant access upon purchase?
                          </FormLabel>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="onboarding_form_id"
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <div className="flex flex-row items-start space-x-3 space-y-0">
                          <Checkbox
                            id="package-onboarding-redirect"
                            checked={!!watched.onboarding_form_id}
                            onCheckedChange={(checked) => {
                              if (!checked) {
                                field.onChange(null);
                                return;
                              }
                              field.onChange(onboardingForms?.[0]?.id ?? null);
                            }}
                            className="mt-0.5"
                          />
                          <div className="space-y-1">
                            <FormLabel
                              htmlFor="package-onboarding-redirect"
                              className="cursor-pointer"
                            >
                              Redirect client into onboarding after checkout
                            </FormLabel>
                          </div>
                        </div>
                        {watched.onboarding_form_id ? (
                          <FormControl>
                            <Select
                              value={field.value ?? NO_ONBOARDING_FORM}
                              onValueChange={(value) =>
                                field.onChange(
                                  value === NO_ONBOARDING_FORM ? null : value
                                )
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select onboarding form" />
                              </SelectTrigger>
                              <SelectContent>
                                {(onboardingForms ?? []).map((formOption) => (
                                  <SelectItem key={formOption.id} value={formOption.id}>
                                    {formOption.name}
                                  </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        ) : null}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="text-right text-sm text-foreground/90">
                  {watched.plan_type === "one-off"
                    ? "Total payout: "
                    : "Recurring payment: "}
                  <span className="font-semibold">
                    {formatCurrency(Number(totalPayout), watched.currency)}
                  </span>
                </div>
              </div>

              <DialogFooter className="mt-4">
                <div className="flex gap-2 w-full justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => handleOnOpenChange(false)}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSaving}>
                    {isSaving ? "Saving..." : initial?.id ? "Update" : "Save"}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PackageDialog;
